/* ==========================================================================
   2ATG — motion registry
   --------------------------------------------------------------------------
   This file exists because of a specific bug documented in
   D:\CLAUDE\SHOPIFY-PLAYBOOK.md §4.8.

   The model theme (nymphaicosmetics.com) has ZERO occurrences of
   shopify:section:load / :unload. When a merchant edits a section in the
   theme customizer, Shopify re-renders that section's HTML — and every
   listener, observer and rAF loop bound to the old nodes is now pointing at
   detached elements. Animations break silently while the client is editing,
   which is the worst possible moment.

   So: every behaviour registers a teardown, and the customizer events drive
   mount/unmount. No exceptions.

   Deliberately no GSAP, no Lenis, no SplitType. Reasons, in order:
     1. The reference does not use them — gear.anduril.com has zero GSAP,
        Lenis, Swiper. It uses native View Transitions and small modules.
     2. The 2ATG contracting site does not use them. Every effect there is
        hand-rolled rAF plus CSS; framer-motion is installed and never
        imported once.
     3. This is a commerce site. LCP and INP convert. ~150 KB of animation
        CDN to power ten moments is a poor trade.
   This is a stated deviation from the playbook, not an oversight.
   ========================================================================== */

(function () {
  "use strict";

  /** root element -> teardown function */
  var registry = new Map();
  /** module name -> mount(root) => teardown|void */
  var modules = Object.create(null);

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  /* ------------------------------------------------------------------
     Registry
     ------------------------------------------------------------------ */

  var booted = false;

  /* A module registered after boot (atg-sections.js, atg-header.js — both
     deferred, both later in the document than this file) still reaches the
     roots already on the page. mount() tears down first, so re-mounting a
     root that carries several modules is safe. */
  function register(name, factory) {
    modules[name] = factory;
    if (!booted) return;
    var nodes = document.querySelectorAll("[data-atg-module]");
    Array.prototype.forEach.call(nodes, function (n) {
      var names = " " + (n.getAttribute("data-atg-module") || "") + " ";
      if (names.indexOf(" " + name + " ") !== -1) mount(n);
    });
  }

  function mount(root) {
    // Tear down anything previously bound to this exact node first.
    unmount(root);

    // Space-separated, so one root can carry several behaviours:
    //   data-atg-module="header nav"
    var names = (root.getAttribute("data-atg-module") || "").split(/\s+/).filter(Boolean);
    if (!names.length) return;

    var teardowns = [];
    names.forEach(function (name) {
      var factory = modules[name];
      if (!factory) return;
      try {
        var t = factory(root);
        if (typeof t === "function") teardowns.push(t);
      } catch (err) {
        // A broken section must not take the rest of the page with it.
        if (window.console) console.error("[atg] mount failed:", name, err);
      }
    });

    registry.set(root, function () {
      teardowns.forEach(function (fn) {
        try { fn(); } catch (e) { /* teardown must never throw */ }
      });
    });
  }

  function unmount(root) {
    var teardown = registry.get(root);
    if (teardown) { teardown(); registry.delete(root); }
  }

  function mountAll(scope) {
    var nodes = (scope || document).querySelectorAll("[data-atg-module]");
    Array.prototype.forEach.call(nodes, mount);
  }

  function unmountAll(scope) {
    var nodes = (scope || document).querySelectorAll("[data-atg-module]");
    Array.prototype.forEach.call(nodes, unmount);
  }

  /* ------------------------------------------------------------------
     Viewport gating.
     Mirrors gsap.matchMedia(): the mount returns its own teardown, and
     the gate runs it when the query stops matching. Mobile gets the
     lighter version and actually releases the desktop listeners.
     ------------------------------------------------------------------ */

  function gate(query, setup) {
    var mq = window.matchMedia(query);
    var active = null;

    function sync() {
      if (mq.matches && !active) {
        active = setup() || function () {};
      } else if (!mq.matches && active) {
        active(); active = null;
      }
    }
    sync();

    if (mq.addEventListener) mq.addEventListener("change", sync);
    else mq.addListener(sync); // Safari < 14

    return function () {
      if (mq.removeEventListener) mq.removeEventListener("change", sync);
      else mq.removeListener(sync);
      if (active) { active(); active = null; }
    };
  }

  /* ------------------------------------------------------------------
     Shared rAF-throttled scroll driver. One listener for the whole page
     rather than one per section.
     ------------------------------------------------------------------ */

  var readers = new Set();
  var ticking = false;
  var scrollBound = false;

  function onFrame() {
    ticking = false;
    readers.forEach(function (fn) {
      try { fn(); } catch (e) { /* keep the loop alive */ }
    });
  }

  function requestTick() {
    if (!ticking) { ticking = true; window.requestAnimationFrame(onFrame); }
  }

  function bindScroll() {
    if (scrollBound) return;
    window.addEventListener("scroll", requestTick, { passive: true });
    window.addEventListener("resize", requestTick, { passive: true });
    scrollBound = true;
  }

  function addReader(fn) {
    bindScroll();
    readers.add(fn);
    fn();
    return function () { readers.delete(fn); };
  }

  /* ==================================================================
     MODULE: reveal
     Scroll-in for grouped children. IntersectionObserver is correct here
     (unlike the sticky index below) because each item is independent and
     we only need a one-shot trigger.
     ================================================================== */

  register("reveal", function (root) {
    var items = root.querySelectorAll("[data-atg-reveal]");
    if (!items.length) return;

    if (reduceMotion.matches) {
      Array.prototype.forEach.call(items, function (el) { el.classList.add("is-revealed"); });
      return;
    }

    var stagger = parseFloat(root.getAttribute("data-atg-stagger")) || 0.06;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var i = parseInt(el.getAttribute("data-atg-index") || "0", 10);
        el.style.transitionDelay = (i * stagger) + "s";
        el.classList.add("is-revealed");
        io.unobserve(el);
      });
    }, { rootMargin: "0px 0px -12% 0px", threshold: 0.1 });

    Array.prototype.forEach.call(items, function (el, i) {
      el.setAttribute("data-atg-index", String(i));
      io.observe(el);
    });

    return function () { io.disconnect(); };
  });

  /* ==================================================================
     MODULE: statement
     The signature effect, ported from
     D:\CLAUDE\2AGT\components\Statement.tsx.

     A paragraph is split to words; scroll position through the block
     drives a lit/unlit boundary sweeping in reading order with a soft
     leading edge. Desktop only — on a phone the block is barely taller
     than the viewport, so there is no travel to animate against.
     ================================================================== */

  var UNLIT = 0.1;
  var FEATHER = 5; // words spanned by the leading edge

  register("statement", function (root) {
    var target = root.querySelector("[data-atg-statement-text]");
    if (!target) return;

    // Split once, keep the original so teardown can restore it verbatim.
    var original = target.innerHTML;
    var words = (target.textContent || "").trim().split(/\s+/);

    target.textContent = "";
    var spans = words.map(function (word, i) {
      var s = document.createElement("span");
      s.textContent = word;
      s.style.display = "inline-block";
      s.style.willChange = "opacity";
      target.appendChild(s);
      if (i < words.length - 1) target.appendChild(document.createTextNode(" "));
      return s;
    });

    function restore() { target.innerHTML = original; }

    if (reduceMotion.matches) {
      spans.forEach(function (s) { s.style.opacity = "1"; });
      return restore;
    }

    var ungate = gate("(min-width: 768px)", function () {
      var release = addReader(function () {
        var r = target.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var line = vh * 0.75;
        var span = line + r.height * 0.25;
        var p = clamp((line - r.top) / span, 0, 1);
        var lit = p * (spans.length + FEATHER);

        for (var i = 0; i < spans.length; i++) {
          var t = clamp((lit - i) / FEATHER, 0, 1);
          spans[i].style.opacity = String(UNLIT + t * (1 - UNLIT));
        }
      });

      return function () {
        release();
        spans.forEach(function (s) { s.style.opacity = ""; s.style.willChange = ""; });
      };
    });

    return function () { ungate(); restore(); };
  });

  /* ==================================================================
     MODULE: sticky-index
     Ported from D:\CLAUDE\2AGT\components\CapabilityIndex.tsx.

     IntersectionObserver was REJECTED there for a documented reason:
     panels sit ~960px apart, so on a short viewport none is ever
     "mostly visible" and on a tall one two are at once. Instead: a
     single crossing line at innerHeight/3, and the last panel whose top
     has crossed it wins. Keep it that way.
     ================================================================== */

  register("sticky-index", function (root) {
    var links  = root.querySelectorAll("[data-atg-index-link]");
    var panels = document.querySelectorAll("[data-atg-index-panel]");
    if (!links.length || !panels.length) return;

    var current = -1;

    return addReader(function () {
      var line = (window.innerHeight || 0) / 3;
      var next = 0;

      for (var i = 0; i < panels.length; i++) {
        if (panels[i].getBoundingClientRect().top <= line) next = i;
      }
      if (next === current) return;
      current = next;

      Array.prototype.forEach.call(links, function (link, i) {
        var on = i === current;
        link.classList.toggle("is-current", on);
        if (on) link.setAttribute("aria-current", "true");
        else link.removeAttribute("aria-current");
      });
    });
  });

  /* ==================================================================
     MODULE: header
     Transparent over the hero, solid past it. 24px threshold, matching
     the contracting site's Nav.tsx.
     ================================================================== */

  /* Two jobs since the Forward Observations Group rebuild:

     1. Pin the bar. The bar's wrapper keeps its height in the layout, so its
        position is always the bar's natural position; once that reaches the
        top of the window the bar goes position: fixed (.is-stuck).

     2. On the homepage the header lies over the hero and has to be exactly
        as tall as it, so the bar sits on the hero's bottom edge. The hero's
        height depends on its setting and its content, so it is measured
        here rather than guessed in CSS (which falls back to 100svh). */

  register("header", function (root) {
    var wrap = root.querySelector(".atg-header__barwrap");
    var overlay = root.classList.contains("atg-header--overlay");
    var lastW = 0, lastH = 0;

    function measure() {
      if (!overlay) return;
      var first = document.querySelector("#atg-main > .shopify-section:first-child");
      if (first) root.style.setProperty("--atg-hero-h", first.offsetHeight + "px");
    }

    function onSectionLoad() { window.requestAnimationFrame(measure); }
    window.addEventListener("load", measure);
    document.addEventListener("shopify:section:load", onSectionLoad);

    var off = addReader(function () {
      if (window.innerWidth !== lastW || window.innerHeight !== lastH) {
        lastW = window.innerWidth; lastH = window.innerHeight;
        measure();
      }
      root.classList.toggle("is-scrolled", window.scrollY > 24);
      if (wrap) root.classList.toggle("is-stuck", wrap.getBoundingClientRect().top <= 0);
    });

    return function () {
      off();
      window.removeEventListener("load", measure);
      document.removeEventListener("shopify:section:load", onSectionLoad);
      root.style.removeProperty("--atg-hero-h");
      root.classList.remove("is-stuck", "is-scrolled");
    };
  });

  /* ==================================================================
     MODULE: hero
     Two jobs, both small.

     1. Reduced motion. prefers-reduced-motion cannot be read on the
        server, so the video ships to everyone and is paused here. That
        stops the movement, not the download; Shopify's poster frame is
        already painted underneath, so the hero still reads.

     2. The theme editor. Selecting the section re-runs the markup and a
        freshly parsed <video> does not always resume, which looks like a
        broken hero to whoever is editing. Nudge it.
     ================================================================== */

  register("hero", function (root) {
    var video = root.querySelector("video");
    if (!video) return;

    function apply() {
      if (reduceMotion.matches) {
        video.pause();
        video.removeAttribute("autoplay");
      } else if (video.paused) {
        var p = video.play();
        if (p && p.catch) p.catch(function () { /* autoplay refused; poster stands in */ });
      }
    }

    apply();

    // A page opened in a background tab is refused autoplay and can stay
    // on the poster; start it the moment the tab is shown.
    function onVisible() { if (!document.hidden) apply(); }
    document.addEventListener("visibilitychange", onVisible);

    if (reduceMotion.addEventListener) reduceMotion.addEventListener("change", apply);

    return function () {
      document.removeEventListener("visibilitychange", onVisible);
      if (reduceMotion.removeEventListener) reduceMotion.removeEventListener("change", apply);
    };
  });

  /* ------------------------------------------------------------------
     Boot + theme editor lifecycle
     ------------------------------------------------------------------ */

  function boot() {
    if (booted) return;
    booted = true;
    mountAll(document);
  }

  /* Boot on DOMContentLoaded, not on execution. This file is deferred, and a
     deferred script runs while readyState is already "interactive" — booting
     then mounted every root before the other deferred files had registered
     their modules, so the nav drawer, the filter tabs and the loadout
     hotspots never came alive. DOMContentLoaded fires after all deferred
     scripts have run. `complete` covers a late injection. */
  if (document.readyState === "complete") {
    boot();
  } else {
    document.addEventListener("DOMContentLoaded", boot);
    window.addEventListener("load", boot);
  }

  // THE POINT OF THIS FILE.
  document.addEventListener("shopify:section:load", function (e) {
    mountAll(e.target);
    // The section root itself may carry the attribute.
    if (e.target.hasAttribute && e.target.hasAttribute("data-atg-module")) mount(e.target);
  });

  document.addEventListener("shopify:section:unload", function (e) {
    unmountAll(e.target);
    if (e.target.hasAttribute && e.target.hasAttribute("data-atg-module")) unmount(e.target);
  });

  // Blocks re-render without a full section reload in some themes; re-mount
  // defensively. mount() tears down first, so this is idempotent.
  document.addEventListener("shopify:block:select", function (e) { mountAll(e.target); });

  window.ATG = { register: register, mount: mount, unmount: unmount, mountAll: mountAll, gate: gate, addReader: addReader };
})();
